import { useState } from 'react';
import type {
  DecoratedLabUser,
  DecoratedPracticeUser,
} from '@witmetrics/utils';
import {
  useActiveAccount,
  useLabUsers,
  usePracticeUsers,
} from '@witmetrics/data-store';
import { mapToKey, sortAlphabeticalByKey } from '@witmetrics/utils/arrays';
import { isNullish } from '@witmetrics/utils/misc';
import { ACCOUNT_TYPES } from '@witmetrics/utils/constants';
import { useIds } from '@/hooks/useIds';
import Menu from '@/components/Menu';
import Divider from '@/components/Divider';
import Button from '@/components/Button';
import FiltersList from '@/components/FiltersList';
import { ALL_STATUSES, FILTER_OPTIONS } from './constants';
import { classes, formatUserOption } from './utils';

export type TasksFilterMenuProps = {
  open: boolean;
  anchorTo: HTMLElement | null;
  statusFilters?: boolean[];
  userFilters: number[];
  onApplyFilters: (filters: {
    statusFilters: boolean[];
    userFilters: number[];
  }) => void;
  onClose: () => void;
};

export default function TasksFilterMenu({
  open,
  anchorTo,
  statusFilters,
  userFilters,
  onApplyFilters,
  onClose,
}: TasksFilterMenuProps) {
  const activeAccount = useActiveAccount();
  const practiceUsers = usePracticeUsers();
  const labUsers = useLabUsers();
  const [statuses, setStatuses] = useState(statusFilters);
  const [userIds, setUserIds] = useIds(userFilters);

  const users: (DecoratedLabUser | DecoratedPracticeUser)[] =
    activeAccount?.accountType === ACCOUNT_TYPES.PRACTICE
      ? practiceUsers
      : labUsers;

  const handleApply = () => {
    onApplyFilters({
      statusFilters: statuses || [],
      userFilters: userIds,
    });
    onClose();
  };

  const handleReset = () => {
    const statusFilters = ALL_STATUSES;
    const userFilters = mapToKey(users, 'userId');
    setStatuses(statusFilters);
    setUserIds(userFilters);
    onApplyFilters({ statusFilters, userFilters });
    onClose();
  };

  const buildUserList = () => {
    return sortAlphabeticalByKey(users.map(formatUserOption), 'label');
  };

  return (
    <Menu anchorTo={anchorTo} open={open} onClose={onClose}>
      <div className={classes.filtersWrapper}>
        {!isNullish(statuses) && (
          <>
            <FiltersList
              className={classes.filtersList}
              name="statuses"
              title="Status"
              selected={statuses}
              options={FILTER_OPTIONS}
              onChange={(update) => setStatuses(update.statuses)}
            />
            <Divider flexItem orientation="vertical" />
          </>
        )}
        <FiltersList
          filter
          className={classes.filtersList}
          name="assignedTo"
          title="Assigned To"
          selected={userIds}
          options={buildUserList()}
          onChange={(update) => setUserIds(update.assignedTo)}
        />
      </div>
      <Divider />
      <div className={classes.buttonsWrapper}>
        <Button
          className={classes.resetButton}
          variant="ghost"
          onClick={handleReset}>
          Reset
        </Button>
        <Button className={classes.applyButton} onClick={handleApply}>
          Apply
        </Button>
      </div>
    </Menu>
  );
}
